import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Upload, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import { formatCurrency, fmtNum } from '@/utils/currency';

export default function Inventory() {
  const { token } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '',
    description: '',
    price: '',
    cost: '',
    quantity: '',
    category_id: '',
    supplier: '',
    images: [],
    show_on_website: false,
    is_taxable: false,
    tiktok_url: '',
    instagram_url: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [taxSettings, setTaxSettings] = useState({ gst_enabled: false, gst_percent: 0 });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchTaxSettings();
  }, []);

  const fetchTaxSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/tax`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxSettings(response.data);
    } catch (error) {
      console.error('Failed to load tax settings');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        cost: formData.cost ? parseFloat(formData.cost) : null,
        quantity: parseInt(formData.quantity)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
      }

      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    const uploadedImages = [];

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedImages.push(response.data.image_url);
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      
      toast.success(`${uploadedImages.length} image(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload some images');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku: product.sku,
      barcode: product.barcode || '',
      description: product.description || '',
      price: product.price.toString(),
      cost: product.cost?.toString() || '',
      quantity: product.quantity.toString(),
      category_id: product.category_id || '',
      supplier: product.supplier || '',
      images: product.images || [],
      show_on_website: product.show_on_website,
      is_taxable: product.is_taxable || false,
      tiktok_url: product.tiktok_url || '',
      instagram_url: product.instagram_url || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      barcode: '',
      description: '',
      price: '',
      cost: '',
      quantity: '',
      category_id: '',
      supplier: '',
      images: [],
      show_on_website: false,
      is_taxable: false,
      tiktok_url: '',
      instagram_url: ''
    });
    setEditingProduct(null);
  };

  const handleExportPDF = () => {
    const rows = products.map((p, i) => [
      i + 1,
      p.name,
      p.sku || '-',
      p.category_name || '-',
      `$${fmtNum(p.selling_price || 0)}`,
      p.quantity || 0,
      `$${fmtNum((p.selling_price || 0) * (p.quantity || 0))}`
    ]);
    const total = products.reduce((s, p) => s + (p.selling_price || 0) * (p.quantity || 0), 0);
    exportListPDF({
      title: 'Inventory List',
      headers: ['#', 'Item Name', 'SKU', 'Category', 'Price', 'Qty', 'Total'],
      rows,
      totalLabel: 'Grand Total',
      totalValue: `$${fmtNum(total)}`,
      token,
      apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="inventory-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Inventory Management</h1>
            <p className="text-slate-600 mt-1">{products.length} products in stock</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportPDF} data-testid="inventory-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-product-button">
                <Plus className="w-5 h-5 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div>
                    <Label>SKU *</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      required
                      data-testid="product-sku-input"
                    />
                  </div>
                  <div>
                    <Label>Barcode</Label>
                    <Input
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Price *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <Label>Cost</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                      data-testid="product-quantity-input"
                    />
                  </div>
                  <div>
                    <Label>Supplier</Label>
                    <Input
                      value={formData.supplier}
                      onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Product Images</Label>
                  <div className="mt-2 space-y-3">
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {formData.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Product ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg border-2 border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        id="product-images-upload"
                        disabled={uploadingImage}
                      />
                      <label htmlFor="product-images-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={() => document.getElementById('product-images-upload').click()}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? 'Uploading...' : 'Upload Images (Max 5MB each)'}
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-semibold">Show on Website</Label>
                    <p className="text-sm text-slate-600">Make this product visible on your store</p>
                  </div>
                  <Switch
                    checked={formData.show_on_website}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_on_website: checked })}
                    data-testid="show-on-website-toggle"
                  />
                </div>

                {/* GST Taxable Toggle */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex-1">
                    <Label className="font-semibold text-emerald-900">GST Taxable</Label>
                    <p className="text-sm text-emerald-700">
                      {taxSettings.gst_enabled 
                        ? `Apply ${taxSettings.gst_percent}% GST on this product`
                        : 'GST is currently disabled in Tax settings'
                      }
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_taxable}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_taxable: checked })}
                    disabled={!taxSettings.gst_enabled}
                    data-testid="is-taxable-toggle"
                  />
                </div>

                {/* Social Media Videos */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <Label className="font-semibold text-base">Social Media Videos (Optional)</Label>
                  <div>
                    <Label>TikTok Video URL</Label>
                    <Input
                      value={formData.tiktok_url}
                      onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                      placeholder="https://www.tiktok.com/@username/video/..."
                    />
                    <p className="text-xs text-slate-500 mt-1">Paste the TikTok video link</p>
                  </div>
                  <div>
                    <Label>Instagram Video URL</Label>
                    <Input
                      value={formData.instagram_url}
                      onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                      placeholder="https://www.instagram.com/p/..."
                    />
                    <p className="text-xs text-slate-500 mt-1">Paste the Instagram post/reel link</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-product-button">
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product) => (
            <Card key={product.id} className="p-4 hover:shadow-lg transition-all" data-testid={`product-card-${product.sku}`}>
              <div className="space-y-3">
                <div className="w-full h-32 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="w-16 h-16 text-slate-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{product.name}</h3>
                  <p className="text-sm text-slate-600">SKU: {product.sku}</p>
                  <div className="flex gap-2 mt-1">
                    {product.show_on_website && (
                      <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                        On Website
                      </span>
                    )}
                    {product.is_taxable && (
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        GST
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(product.price)}</p>
                    <p className="text-sm text-slate-600">Stock: {product.quantity}</p>
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(product)}
                    className="flex-1"
                    data-testid={`edit-product-${product.sku}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 text-red-600 hover:bg-red-50"
                    data-testid={`delete-product-${product.sku}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}